/** @jsx jsx */
import { jsx } from '@emotion/core';
import { CircularProgress } from '@material-ui/core';
import { tableToolbarStyles } from 'components/TableToolbar/TableToolbar.style';
import { FormApi } from 'final-form';
import { ChangeEvent, Component, Fragment } from 'react';
import { Field } from 'react-final-form';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { AppDispatch, AppState } from 'store';
import {
  getCustomerSpinnerSelector,
  getDefaultLanguagesSpinnerSelector,
  getTemplateSpinnerSelector,
} from 'store/spinner/spinner.selectors';
import {
  clearDefaultUserLanguages,
  clearXtmTemplates,
  getDefaultUserLanguages,
  xtmGetCustomers,
  xtmGetCustomersById,
  xtmGetTemplates,
  xtmGetTemplatesById,
} from 'store/user/user.actions';
import { IGetLanguages } from 'store/user/user.interface';
import {
  getDefaultUserLanguagesSelector,
  getXtmCustomersSelector,
  getXtmTemplatesSelector,
} from 'store/user/user.selectors';
import {
  composeValidators,
  isAllowedPrefix,
  required,
} from 'utils/customValidators';
import { isObjectEqual } from 'utils/objectEquality';
import {
  LanguageDTO,
  UserDTO,
  XTMConnectDTO,
  XTMCustomerDTO,
  XTMTemplateDTO,
} from 'utils/restApplicationClient';
import { IForm } from '../../../containers/AddUser/AddUserContainer';
import { EditData } from '../CilentData/EditData';
import CustomCheckbox from '../Input/CustomCheckbox';
import CustomFieldRow from '../Input/CustomFieldRow';
import CustomSelect from '../Input/CustomSelect';

interface IProps {
  native?: boolean;
  authorizationId?: string;
  user?: UserDTO;
  form: FormApi<IForm>;
}

interface IDispatchProps {
  getCustomers: (payload: XTMConnectDTO) => AppDispatch;
  getTemplates: (payload: {
    xtmAuthId: string;
    xtmCustomerId: string;
  }) => AppDispatch;
  getCustomersById: (payload: string) => AppDispatch;
  getTemplatesById: (payload: {
    userId: string;
    queryParams: { xtmCustomerId: string };
  }) => AppDispatch;
  clearTemplates: () => AppDispatch;
  getDefaultUserLanguages: (payload: IGetLanguages) => AppDispatch;
  clearDefaultUserLanguages: () => AppDispatch;
}

interface IStateProps {
  customers?: XTMCustomerDTO[];
  templates?: XTMTemplateDTO[];
  customerSpinner: boolean;
  templateSpinner: boolean;
  languages: LanguageDTO[];
  defaultUserLanguagesSpinner: boolean;
}

type PropType = IProps & IDispatchProps & IStateProps;

export class ConnectedFields extends Component<PropType> {
  componentDidMount(): void {
    const {
      authorizationId,
      getCustomers,
      getCustomersById,
      user,
      getTemplatesById,
    } = this.props;
    if (authorizationId) {
      getCustomers({ xtmAuthorizationId: authorizationId });
    } else if (user && user.id) {
      getCustomersById(user.id);
      if (user.xtmProperties.xtmCustomerId) {
        getTemplatesById({
          userId: user.id,
          queryParams: {
            xtmCustomerId: user.xtmProperties.xtmCustomerId.toString(),
          },
        });
      }
    }
  }

  componentDidUpdate(previousProps: PropType): void {
    const { authorizationId, getDefaultUserLanguages, form, user } = this.props;
    const { interfaceLanguage, xtmAuthorizationId: xtmAuthId } =
      form.getState().values;

    if (
      this.props.customers &&
      previousProps.customers &&
      this.shouldUpdate(previousProps.customers, this.props.customers) &&
      authorizationId
    ) {
      this.props.form.change('xtmCustomerId', undefined);
      this.props.form.change('xtmTemplateId', undefined);
      this.props.form.change('defaultTranslationSourceLanguage', undefined);
      this.props.clearTemplates();
      this.props.clearDefaultUserLanguages();
    }

    if (
      previousProps.authorizationId !== authorizationId &&
      authorizationId &&
      user
    ) {
      getDefaultUserLanguages({
        xtmCustomerId: user.xtmProperties.xtmCustomerId.toString(),
        queryParams: {
          interfaceLanguage,
          xtmAuthId,
        },
      });
    }
  }

  shouldUpdate(
    previousCustomers: XTMCustomerDTO[],
    customers: XTMCustomerDTO[],
  ): boolean {
    if (previousCustomers.length !== customers.length) {
      return true;
    }
    return !customers.reduce(
      (previous: boolean, current: XTMCustomerDTO, index) => {
        return (
          isObjectEqual(
            current as unknown as Record<string, unknown>,
            previousCustomers[index] as unknown as Record<string, unknown>,
          ) && previous
        );
      },
      true,
    );
  }

  mapCustomersToRows(
    customers?: XTMCustomerDTO[],
  ): Array<{ value: string; name: string }> {
    if (!customers) {
      return [];
    }
    return customers.map((customer) => ({
      value: customer.xtmCustomerId.toString(),
      name: customer.name,
    }));
  }

  mapTemplatesToRows(
    templates?: XTMTemplateDTO[],
  ): Array<{ value: string; name: string }> {
    if (!templates) {
      return [];
    }
    return templates.map((template) => ({
      value: template.xtmTemplateId.toString(),
      name: template.name,
    }));
  }
  mapUsersLanguages(
    languages: LanguageDTO[],
  ): Array<{ value: string; name: string }> {
    if (!languages) {
      return [];
    }
    return languages.map((languages) => ({
      value: languages.languageCode,
      name: languages.name,
    }));
  }

  handleCustomerChange = (
    event: ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ): void => {
    const {
      authorizationId,
      getTemplates,
      getDefaultUserLanguages,
      getTemplatesById,
      user,
      form,
    } = this.props;
    const { interfaceLanguage, xtmAuthorizationId: xtmAuthId } =
      form.getState().values;
    const xtmCustomerId = event.target.value as string;

    form.change('defaultTranslationSourceLanguage', undefined);
    form.change('xtmTemplateId', undefined);

    if (authorizationId) {
      getTemplates({
        xtmAuthId: authorizationId,
        xtmCustomerId,
      });
    } else if (user && user.id) {
      getTemplatesById({
        userId: user.id,
        queryParams: { xtmCustomerId: event.target.value as string },
      });
    }
    getDefaultUserLanguages({
      xtmCustomerId,
      queryParams: {
        interfaceLanguage,
        xtmAuthId,
      },
    });
  };

  selectSpinner = (): JSX.Element => {
    return (
      <CircularProgress
        data-testid="connectedFieldsSpinner"
        color="secondary"
        size={20}
        css={tableToolbarStyles.endAdornmentSpinner}
      />
    );
  };

  render(): JSX.Element {
    const {
      native,
      customers,
      templates,
      customerSpinner,
      templateSpinner,
      user,
      languages,
      defaultUserLanguagesSpinner,
    } = this.props;
    return (
      <Fragment>
        <Field
          name="xtmCustomerId"
          validate={required}
          initialValue={
            user &&
            customers &&
            user.xtmProperties &&
            user.xtmProperties.xtmCustomerId
              ? user.xtmProperties.xtmCustomerId.toString()
              : undefined
          }
        >
          {({ input, meta }): JSX.Element => (
            <CustomSelect
              label="users.xtm.defaultCustomer"
              error={meta.error}
              touched={meta.touched}
              inputProps={{ ...input }}
              testId="defaultCustomer"
              rows={this.mapCustomersToRows(customers)}
              selectProps={{
                native,
                disabled: !customers,
                IconComponent: customerSpinner ? this.selectSpinner : undefined,
              }}
              onChange={this.handleCustomerChange}
            />
          )}
        </Field>
        <Field
          name="xtmTemplateId"
          validate={required}
          initialValue={
            user &&
            templates &&
            user.xtmProperties &&
            user.xtmProperties.xtmTemplateId
              ? user.xtmProperties.xtmTemplateId.toString()
              : undefined
          }
        >
          {({ input, meta }): JSX.Element => (
            <CustomSelect
              label="users.xtm.defaultTemplate"
              error={meta.error}
              touched={meta.touched}
              inputProps={{ ...input }}
              testId="defaultTemplate"
              rows={this.mapTemplatesToRows(templates)}
              selectProps={{
                native,
                disabled: !templates,
                IconComponent: templateSpinner ? this.selectSpinner : undefined,
              }}
            />
          )}
        </Field>
        <Field
          name="xtmProjectNamePrefix"
          validate={composeValidators([required, isAllowedPrefix])}
          initialValue={
            user &&
            user.xtmProperties &&
            user.xtmProperties.xtmProjectNamePrefix
          }
        >
          {({ input, meta }): JSX.Element => (
            <CustomFieldRow
              label="users.xtm.projectNamePrefix"
              error={meta.error}
              touched={meta.touched}
              inputProps={input}
              testId="prefixNameInput"
            />
          )}
        </Field>
        <Field
          name="defaultTranslationSourceLanguage"
          initialValue={
            user && user.userSpecification.defaultTranslationSourceLanguage
          }
          validate={required}
        >
          {({ input, meta }): JSX.Element => (
            <CustomSelect
              label="common.defaultCmcLang"
              error={meta.error}
              touched={meta.touched}
              inputProps={input}
              testId="defaultLanguageSelect"
              rows={this.mapUsersLanguages(languages)}
              width="50%"
              selectProps={{
                native,
                disabled: !languages,
                IconComponent: defaultUserLanguagesSpinner
                  ? this.selectSpinner
                  : undefined,
              }}
            />
          )}
        </Field>
        <Field
          name="forceDefaultCustomerSettings"
          type="checkbox"
          initialValue={user?.userSpecification?.forceDefaultCustomerSettings}
        >
          {({ input }): JSX.Element => (
            <CustomCheckbox
              label="common.forceDefaultCustomerSettings"
              inputProps={{ ...input }}
              testId="checkboxInput"
            />
          )}
        </Field>
        {user && (
          <EditData createdAt={user.createdAt} modifiedAt={user.modifiedAt} />
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  customers: getXtmCustomersSelector(state),
  templates: getXtmTemplatesSelector(state),
  customerSpinner: getCustomerSpinnerSelector(state),
  templateSpinner: getTemplateSpinnerSelector(state),
  languages: getDefaultUserLanguagesSelector(state),
  defaultUserLanguagesSpinner: getDefaultLanguagesSpinnerSelector(state),
});

const mapDispatchToProps = (
  dispatch: Dispatch<AppDispatch>,
): IDispatchProps => ({
  getCustomers: (payload: XTMConnectDTO): AppDispatch =>
    dispatch(xtmGetCustomers(payload)),
  getTemplates: (payload: {
    xtmAuthId: string;
    xtmCustomerId: string;
  }): AppDispatch => dispatch(xtmGetTemplates(payload)),
  getCustomersById: (payload: string): AppDispatch =>
    dispatch(xtmGetCustomersById(payload)),
  getTemplatesById: (payload: {
    userId: string;
    queryParams: { xtmCustomerId: string };
  }): AppDispatch => dispatch(xtmGetTemplatesById(payload)),
  clearTemplates: (): AppDispatch => dispatch(clearXtmTemplates()),
  getDefaultUserLanguages: (payload: IGetLanguages): AppDispatch =>
    dispatch(getDefaultUserLanguages(payload)),
  clearDefaultUserLanguages: (): AppDispatch =>
    dispatch(clearDefaultUserLanguages()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConnectedFields);
